import React, {useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {EulaAgreementContent} from "./EulaDialog.styled";
import {sanitizeHtml} from "../../../utils/format-data";
import {EulaAgreementResponse} from "../../../services/terms.service";
import CloseIcon from "@mui/icons-material/Close";
import {formatDateWithLanguage} from "../../../utils/format-date";

interface EulaDialogPropsType {
    open: boolean;
    eulaData: EulaAgreementResponse | null;
    onClose?: () => void;
    isRequired?: boolean;
    onAccept?: (accepted: boolean) => Promise<void>;
}

const EulaDialog = ({open, onClose, eulaData, isRequired = false, onAccept}: EulaDialogPropsType) => {
    const {t} = useTranslation();
    const [accepted, setAccepted] = useState<boolean>(false);

    const handleNextWithAccept = async () => {
        if (onAccept) {
            await onAccept(accepted);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isRequired ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            aria-labelledby="eula-dialog-title"
            aria-modal="true"
            disableEscapeKeyDown
        >
            <DialogTitle id="eula-dialog-title" sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                {t('eulaDialog.title')}

                {!isRequired && (
                    <Tooltip title={t('common.close')}>
                        <IconButton onClick={onClose} size="small" aria-label={t('common.close') as string}>
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                )}
            </DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                <Typography variant="caption" color="text.secondary">
                    {t('eulaDialog.languageInfo')}
                </Typography>

                <Typography variant="subtitle1" color="text.primary">
                    {t('eulaDialog.fullInfo')}
                </Typography>

                <EulaAgreementContent>
                    {!eulaData || eulaData.content.length === 0 && (
                        <Typography>{t('eulaDialog.noAgreement')}</Typography>
                    )}

                    {eulaData && eulaData.content.length > 0 && (
                        <Box sx={{ mt: 2 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(eulaData.content) }} />
                    )}
                </EulaAgreementContent>

                {eulaData && (
                    <Typography variant="caption" color="text.hint">
                        {t('eulaDialog.version') + ': ' + eulaData.version} &middot; {t('eulaDialog.updateDate') + ': ' + formatDateWithLanguage(eulaData.updatedAt)}
                    </Typography>
                )}

                {(isRequired && onAccept) && (
                    <FormControlLabel
                        value={accepted}
                        control={
                            <Checkbox
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                            />
                        }
                        label={t('eulaDialog.checkboxLabel')}
                        labelPlacement="end"
                        sx={{ mt: 2, gap: '8px', alignSelf: 'center' }}
                    />
                )}
            </DialogContent>

            {(isRequired && onAccept) && (
                <DialogActions>
                    <Button variant="contained" disabled={!accepted} onClick={handleNextWithAccept}>
                        {t('common.save')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default EulaDialog;